/* The price sheet (owner request 2026-10-30): "we upload the price Excel
 * sheet, then it sets all the prices by itself".
 *
 * Download your price list as an Excel workbook — one row per type of every
 * service you offer, your price beside it — fill in "Your price (₹)", and
 * upload it. The server checks every row and nothing is written until you
 * press Apply; then every new and changed price goes in force at once, all or
 * none (POST /agency/prices/sheet/check, then /apply). A blank price cell
 * leaves that price as it is; "Stop offering" on the list below is how a
 * price stops. Owners and admins upload; anyone may download. */

import { useRef, useState } from 'react';
import { applyPriceSheet, checkPriceSheet, downloadPriceSheet, type PriceSheetCheck, type PriceSheetRow } from '../data/agency';
import { errorText } from '../data/words';
import { money } from '../data/format';
import { Btn, Card, CardHead, CellStack, Note, Table, Tag, TRow, uiCss } from '../ui';

const COLS = '0.5fr 1.8fr 1.4fr 1.4fr 2fr';

type SheetStatus = 'NEW' | 'CHANGED' | 'UNCHANGED' | 'BLANK' | 'ERROR';
const STATUS: Record<SheetStatus, { word: string; fg: string; bg: string }> & Partial<Record<string, { word: string; fg: string; bg: string }>> = {
  NEW: { word: 'New price', fg: 'var(--teal)', bg: 'var(--teal-bg)' },
  CHANGED: { word: 'Changes', fg: 'var(--amber)', bg: 'var(--amber-bg)' },
  UNCHANGED: { word: 'Same as now', fg: 'var(--ink-soft)', bg: 'var(--surface-field)' },
  BLANK: { word: 'Left as it is', fg: 'var(--ink-soft)', bg: 'var(--surface-field)' },
  ERROR: { word: 'Fix this row', fg: 'var(--danger)', bg: 'var(--danger-bg)' },
};

function amountText(base?: string | null, inc?: string | null, perKm?: string | null): string {
  if (!base) return '—';
  if (inc != null && perKm) return `${money(base)} for ${Number(inc)} km, then ${money(perKm)}/km`;
  return money(base);
}

export function PriceSheetCard({ manage, onApplied }: { manage: boolean; onApplied: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [check, setCheck] = useState<PriceSheetCheck | null>(null);
  const [busy, setBusy] = useState<'download' | 'check' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const download = async () => {
    setBusy('download');
    setError(null);
    try {
      const blob = await downloadPriceSheet();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-prices-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const upload = async (f: File) => {
    setBusy('check');
    setError(null);
    setDone(null);
    setCheck(null);
    setFile(f);
    try {
      setCheck(await checkPriceSheet(f));
    } catch (e) {
      setFile(null);
      setError(errorText(e));
    } finally {
      setBusy(null);
      if (input.current) input.current.value = '';
    }
  };

  const apply = async () => {
    if (!file) return;
    setBusy('apply');
    setError(null);
    try {
      const out = await applyPriceSheet(file);
      setDone(`${out.applied} price${out.applied === 1 ? '' : 's'} set from the sheet. Families see them for new bookings from now.`);
      setCheck(null);
      setFile(null);
      onApplied();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const toApply = check ? check.new + check.changed : 0;
  const rows: PriceSheetRow[] = check ? [...check.rows].sort((a, b) => order(a.status) - order(b.status) || a.line - b.line) : [];

  return (
    <Card>
      <CardHead
        title="Price sheet (Excel)"
        sub="Set many prices at once: download your price list, fill in “Your price (₹)”, and upload it. Every row is checked first; nothing changes until you press Apply."
        right={
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn size="sm" disabled={busy !== null} onClick={() => void download()}>
              {busy === 'download' ? 'Preparing…' : 'Download price sheet'}
            </Btn>
            {manage ? (
              <Btn size="sm" variant="primary" disabled={busy !== null} onClick={() => input.current?.click()}>
                {busy === 'check' ? 'Checking…' : 'Upload filled sheet'}
              </Btn>
            ) : null}
          </span>
        }
      />
      <input
        ref={input}
        type="file"
        accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        style={{ display: 'none' }}
        aria-label="The filled price sheet"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
        }}
      />
      <div style={{ padding: '0 18px 18px', display: 'grid', gap: 12 }}>
        {error ? <Note tone="danger">{error}</Note> : null}
        {done ? <Note tone="ok">{done}</Note> : null}
        {!check && !error && !done ? (
          <div className={uiCss.sub}>
            Leave a price cell empty to keep that price as it is. An ambulance row also needs “Km included” and “₹ per km beyond”.
          </div>
        ) : null}
        {check ? (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ font: '700 13px var(--font-ui)' }}>{file?.name}</span>
              {(['NEW', 'CHANGED', 'UNCHANGED', 'BLANK', 'ERROR'] as const).map((s) => {
                const n = { NEW: check.new, CHANGED: check.changed, UNCHANGED: check.unchanged, BLANK: check.blank, ERROR: check.errors }[s];
                return n ? (
                  <Tag key={s} fg={STATUS[s].fg} bg={STATUS[s].bg}>
                    {n} · {STATUS[s].word.toLowerCase()}
                  </Tag>
                ) : null;
              })}
            </div>
            {check.errors ? (
              <Note tone="danger">
                {check.errors === 1 ? 'One row needs' : `${check.errors} rows need`} fixing. Correct {check.errors === 1 ? 'it' : 'them'} in the sheet and upload it
                again — nothing is changed while any row is wrong.
              </Note>
            ) : toApply === 0 ? (
              <Note tone="info">Nothing to change: every price in the sheet is the same as now, or left empty.</Note>
            ) : null}
            <Table cols={COLS} head={['Row', 'Type', 'Now', 'From the sheet', 'Result']} minWidth={720}>
              {rows.map((r) => (
                <TRow key={r.line} cols={COLS}>
                  <span className={uiCss.mono}>{r.line}</span>
                  <CellStack top={r.variant_name || `${r.service_code} / ${r.variant_code}`} sub={r.variant_name ? `${r.service_code} / ${r.variant_code}` : undefined} />
                  <span>{amountText(r.current_base_amount, r.current_included_km, r.current_per_km_amount)}</span>
                  <span>{r.status === 'BLANK' ? '—' : amountText(r.base_amount, r.included_km, r.per_km_amount)}</span>
                  <CellStack
                    top={
                      <Tag fg={STATUS[r.status]?.fg ?? 'var(--ink-soft)'} bg={STATUS[r.status]?.bg ?? 'var(--surface-field)'}>
                        {STATUS[r.status]?.word ?? r.status}
                      </Tag>
                    }
                    sub={r.message}
                  />
                </TRow>
              ))}
            </Table>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn
                disabled={busy !== null}
                onClick={() => {
                  setCheck(null);
                  setFile(null);
                }}
              >
                Cancel
              </Btn>
              <Btn variant="primary" disabled={busy !== null || check.errors > 0 || toApply === 0} onClick={() => void apply()}>
                {busy === 'apply' ? 'Setting prices…' : `Apply ${toApply} price${toApply === 1 ? '' : 's'}`}
              </Btn>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}

// Rows to fix first, then what changes, then the rest.
function order(status: string): number {
  return ({ ERROR: 0, NEW: 1, CHANGED: 2, UNCHANGED: 3, BLANK: 4 } as Record<string, number>)[status] ?? 5;
}
